import { cn, cva, NoddiIconName, type VariantProps } from 'noddi-ui-common';
import { Card, CardContent } from '../../base/card';
import { cardVariants } from '../../base/cardVariants';
import { NoddiCardHeader } from './NoddiCardHeader';

export type NoddiCardProps = VariantProps<typeof cardVariants> & {
  className?: string;
  children?: React.ReactNode;
  headerNode?: React.ReactNode;
  title?: string;
  description?: string;
  contentClassName?: string;
  size?: 'small' | 'medium';
  icon?: NoddiIconName;
  titleCount?: number;
  tooltip?: string | React.ReactNode;
  descriptionSkeleton?: boolean;
  /** Seats the title icon in a washed-brand tile instead of rendering it bare. */
  iconTile?: boolean;
  /**
   * Edge-to-edge brand-tinted strip directly under the header: the one call to action the card is
   * asking for (e.g. "How was your experience?"), kept inside the card rather than nested as a card of its own.
   */
  banner?: React.ReactNode;
  /** Edge-to-edge muted strip closing the card: secondary actions and attribution, below the content. */
  footer?: React.ReactNode;
};

export function NoddiCard({
  title,
  description,
  children,
  headerNode,
  className,
  contentClassName,
  size = 'medium',
  icon,
  titleCount,
  tooltip,
  variant,
  descriptionSkeleton,
  iconTile,
  banner,
  footer
}: NoddiCardProps) {
  const hasHeader = !!(title || headerNode || description || descriptionSkeleton);

  const isSmall = size === 'small';
  return (
    <Card variant={variant} className={cn(isSmall && 'gap-3 p-4', className)}>
      {hasHeader && (
        <NoddiCardHeader
          title={title}
          description={description}
          icon={icon}
          headerNode={headerNode}
          isSmall={isSmall}
          titleCount={titleCount}
          tooltip={tooltip}
          descriptionSkeleton={descriptionSkeleton}
          iconTile={iconTile}
        />
      )}
      {banner && (
        <div className={cn(flushBandVariants({ size }), 'border-y border-primary/10 bg-card-light-background')}>
          {banner}
        </div>
      )}
      <CardContent className={contentClassName}>{children}</CardContent>
      {footer && (
        <div className={cn(flushBandVariants({ size, edge: 'bottom' }), 'rounded-b-xl border-t bg-muted/50')}>
          {footer}
        </div>
      )}
    </Card>
  );
}

// Bands cancel the card's own padding (p-5, or p-4 when small) to reach its border, then restore it as their inset.
const flushBandVariants = cva('', {
  variants: {
    size: {
      small: '-mx-4 px-4 py-3',
      medium: '-mx-5 px-5 py-4'
    },
    edge: {
      bottom: ''
    }
  },
  compoundVariants: [
    { size: 'small', edge: 'bottom', class: '-mb-4' },
    { size: 'medium', edge: 'bottom', class: '-mb-5' }
  ],
  defaultVariants: {
    size: 'medium'
  }
});
